// Distance maps: for a target (a set of hexes) and a kind of mover (its Move, and the zones that stop
// it), the activations needed from every state (hex and facing). Built once per decision by working
// backwards from the target; any unit's route length is then a lookup. Corridors: the hexes on any of
// a unit's best routes, for judging whether a zone stands in its way.
//
// The rules are those of the forward search (movement.ts, reachFrom): a step ahead or a 60° turn costs
// 1 Move; stepping into a stopping zone ends the activation's movement; a unit that starts in a zone,
// or turns, moves on freely. Bodies don't block (they move). A test checks maps against the forward
// search on the bench positions.

import { DSTEP, newStamp, reachFrom } from './movement';

export const MAX_ACTIVATIONS = 5;
export const UNREACHABLE = 127;

// all: activations from each state with every activation stopped by `later`; first: the same when
// the first activation is stopped by `first` instead (a unit that can still act this round meets only
// the zones that are up now). Stored over the map's states only (index: see stateIndex).
export interface DistanceMap { all: Int8Array; first: Int8Array; index: Int16Array }

const SIZE = 64 * 64 * 8;

// Each on-map hex's place in a compact table (-1 off the map), per map.
const indexCache = new WeakMap<Uint8Array, { index: Int16Array; hexes: number }>();
function stateIndex(map: Uint8Array) {
  let c = indexCache.get(map);
  if (!c) {
    const index = new Int16Array(64 * 64).fill(-1);
    let n = 0;
    for (let h = 0; h < 64 * 64; h++) if (map[h]) index[h] = n++;
    c = { index, hexes: n };
    indexCache.set(map, c);
  }
  return c;
}
const at = (dm: DistanceMap, st: number) => dm.index[st >> 3] * 8 + (st & 7);
export const mapValue = (dm: DistanceMap, st: number, thisRound = false) => (dm.index[st >> 3] < 0 ? UNREACHABLE : (thisRound ? dm.first : dm.all)[at(dm, st)]);

export function distanceMap(map: Uint8Array, move: number, targets: number[], first: Set<number>, later: Set<number>): DistanceMap {
  const { index, hexes } = stateIndex(map);
  const all = new Int8Array(hexes * 8).fill(UNREACHABLE);
  const same = first === later;
  const firstMap = same ? all : new Int8Array(hexes * 8).fill(UNREACHABLE);
  const dm: DistanceMap = { all, first: firstMap, index };
  const firstStops = stopTable(first), laterStops = stopTable(later);
  // Level 0: the target hexes, any facing.
  let layer: number[] = [];
  for (const h of targets) if (map[h]) for (let f = 0; f < 6; f++) { const st = h * 8 + f; all[index[h] * 8 + f] = 0; firstMap[index[h] * 8 + f] = 0; layer.push(st); }
  // Level k: states one activation from a level k-1 state. Each level starts from the states the last
  // one added: a state that reaches an older one in an activation already has a lower level, so older
  // states are never crossed.
  for (let k = 1; k <= MAX_ACTIVATIONS && layer.length; k++) {
    if (!same) {
      const n = backReach(map, index, all, k - 1, move, firstStops, layer);
      for (let i = 0; i < n; i++) { const st = buf[i], a = index[st >> 3] * 8 + (st & 7); if (firstMap[a] === UNREACHABLE) firstMap[a] = k; }
    }
    const n = backReach(map, index, all, k - 1, move, laterStops, layer);
    const next: number[] = [];
    for (let i = 0; i < n; i++) { const st = buf[i], a = index[st >> 3] * 8 + (st & 7); if (all[a] === UNREACHABLE) { all[a] = k; next.push(st); } }
    layer = next;
  }
  return dm;
}

// Zones as a per-hex table (1: stops), built once per zone set.
const stopTables = new WeakMap<Set<number>, Uint8Array>();
function stopTable(stops: Set<number>): Uint8Array {
  let t = stopTables.get(stops);
  if (!t) { t = new Uint8Array(64 * 64); stops.forEach(h => { t![h] = 1; }); stopTables.set(stops, t); }
  return t;
}

// The reverse of reachFrom: every state from which one activation of `moves` reaches a goal state (the
// seeds, at `level`; states below it are never crossed). A step into a stopping hex ends movement, so it
// can only be the last step (onto a goal); turns are free. Results go to `buf`; returns how many.
const backSeen = new Int32Array(SIZE), goalAt = new Int32Array(SIZE);
let backStamp = 0;
let buf = new Int32Array(SIZE);
const queue = new Int32Array(SIZE * 2);
function backReach(map: Uint8Array, index: Int16Array, levels: Int8Array, level: number, moves: number, stops: Uint8Array, goals: number[]): number {
  const stamp = ++backStamp;
  let n = 0, head = 0, tail = 0;
  for (const g of goals) if (backSeen[g] !== stamp) { backSeen[g] = stamp; goalAt[g] = stamp; queue[tail++] = g; }
  for (let m = 0; m < moves && head < tail; m++) {
    const end = tail;
    for (; head < end; head++) {
      const y = queue[head], h = y >> 3, f = y & 7;
      for (let d = 0; d < 3; d++) {
        let x: number;
        if (d === 0) x = h * 8 + (f + 1) % 6;
        else if (d === 1) x = h * 8 + (f + 5) % 6;
        else {
          if (goalAt[y] !== stamp && stops[h]) continue;
          const ph = h - DSTEP[f];
          if (!map[ph]) continue;
          x = ph * 8 + f;
        }
        if (backSeen[x] === stamp || levels[index[x >> 3] * 8 + (x & 7)] < level) continue;
        backSeen[x] = stamp;
        buf[n++] = x;
        queue[tail++] = x;
      }
    }
  }
  return n;
}

// A unit's route length from state `start`: the map's value, or MAX_ACTIVATIONS + 1 if it can't get there.
export function lookup(dm: DistanceMap, start: number, thisRound: boolean): number {
  const v = mapValue(dm, start, thisRound);
  return v === UNREACHABLE ? MAX_ACTIVATIONS + 1 : v;
}

// The forward search, for a few exact routes and the tests: activations from `start` onto `targets`,
// the first stopped by `first`, the rest by `later`. With `reachedIn`, records the activation in which
// each state is first reached.
export function forwardTurns(map: Uint8Array, move: number, start: number, targets: Set<number>, first: Set<number>, later: Set<number>, reachedIn?: Map<number, number>): number {
  const stamp = newStamp();
  let frontier = [start];
  reachedIn?.set(start, 1);
  for (let t = 1; t <= MAX_ACTIVATIONS && frontier.length; t++) {
    const next = reachFrom(map, move, t === 1 ? first : later, frontier, stamp);
    if (reachedIn) for (const st of next) reachedIn.set(st, t);
    if (next.some(st => targets.has(st >> 3))) return t;
    frontier = next;
  }
  return MAX_ACTIVATIONS + 1;
}
